<template>
  <div
    v-if="isOpen"
    id="images-modal"
    class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
  >
    <div class="modal-container bg-white w-full max-w-6xl rounded-2xl shadow-2xl overflow-hidden relative animate-modal-in">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <i class="fas fa-images"></i>
          </div>
          <div class="header-text">
            <h2 class="modal-title">{{ placeName }}</h2>
            <span class="modal-subtitle">Image Gallery</span>
          </div>
        </div>
        <button
          @click="closeModal"
          class="close-button"
          aria-label="Close Modal"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Featured Image Container -->
      <div class="image-container">
        <!-- Loading Spinner -->
        <div v-if="loading" class="loading-container">
          <div class="modern-loader"></div>
          <p class="loading-text">Loading image...</p>
        </div>
        
        <!-- Main Image -->
        <div v-if="currentImageUrl" class="main-image-wrapper">
          <img
            v-show="!loading"
            :src="currentImageUrl"
            :alt="images[currentImageIndex]?.title || 'Image'"
            class="main-image"
            @load="onImageLoad"
            @error="onImageError"
          />
          
          <!-- Image Info Overlay -->
          <div v-if="images.length > 0 && images[currentImageIndex] && !loading" class="image-info-overlay">
            <div class="image-counter">
              <span class="counter-text">{{ currentImageIndex + 1 }} / {{ images.length }}</span>
            </div>
            <div v-if="images[currentImageIndex].title" class="image-title">
              {{ images[currentImageIndex].title }}
            </div>
            <a
              v-if="images[currentImageIndex].contextLink"
              :href="images[currentImageIndex].contextLink"
              target="_blank"
              rel="noopener noreferrer"
              class="source-link"
            >
              <i class="fas fa-external-link-alt"></i>
              View Source
            </a>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <button
          v-if="images.length > 1"
          @click="prevImage"
          class="nav-button nav-button-prev"
          aria-label="Previous Image"
        >
          <i class="fas fa-chevron-left"></i>
        </button>

        <button
          v-if="images.length > 1"
          @click="nextImage"
          class="nav-button nav-button-next"
          aria-label="Next Image"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
      <!-- Thumbnail Carousel -->
      <div v-if="images.length > 1" class="thumbnail-carousel">
        <div class="carousel-header">
          <h3 class="carousel-title">
            <i class="fas fa-th-large"></i>
            Gallery
          </h3>
          <span class="image-count">{{ images.length }} images</span>
        </div>
        <div class="thumbnail-container">
          <div
            v-for="(image, index) in images"
            :key="image.link || image.url || index"
            @click="setCurrentImage(index)"
            class="thumbnail-item"
            :class="{ 'thumbnail-active': index === currentImageIndex }"
          >
            <img
              :src="image.thumbnail || image.link || image.url"
              :alt="image.title || 'Thumbnail'"
              class="thumbnail-image"
            />
            <div class="thumbnail-overlay">
              <div class="thumbnail-number">{{ index + 1 }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'ImagesModal',
  computed: {
    ...mapState('images', ['isOpen', 'placeName', 'images', 'currentImageIndex', 'loading']),
    currentImageUrl() {
      if (this.images.length > 0 && this.images[this.currentImageIndex]) {
        const image = this.images[this.currentImageIndex]
        return image.link || image.url || image.src || image.webformatURL
      }
      return null
    }
  },
  methods: {
    ...mapActions('images', ['closeModal', 'setCurrentImage', 'prevImage', 'nextImage', 'onImageLoad', 'onImageError']),
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      } else if (event.key === 'ArrowLeft') {
        this.prevImage();
      } else if (event.key === 'ArrowRight') {
        this.nextImage();
      }
    }
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        document.addEventListener('keydown', this.handleKeydown);
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
      } else {
        document.removeEventListener('keydown', this.handleKeydown);
        document.body.style.overflow = 'auto'; // Restore scrolling
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = 'auto';
  }
}
</script>

<style scoped>
@import '~@fortawesome/fontawesome-free/css/all.css';

/* Modal Animation */
@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-modal-in {
  animation: modalIn 0.3s ease-out;
}

/* Modal Container */
.modal-container {
  max-height: 95vh;
  display: flex;
  flex-direction: column;
}

/* Modal Header */
.modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.header-icon i {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.9);
}

.header-text {
  display: flex;
  flex-direction: column;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.modal-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  margin-top: 2px;
}

.close-button {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.close-button:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.close-button i {
  font-size: 16px;
}

/* Image Container */
.image-container {
  position: relative;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 70vh;
  max-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 20px;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 40px;
}

.modern-loader {
  width: 64px;
  height: 64px;
  border: 6px solid rgba(102, 126, 234, 0.1);
  border-top: 6px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  margin: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Main Image */
.main-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image {
  max-width: 100%;
  max-height: calc(70vh - 120px); /* Account for padding and overlay space */
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.main-image:hover {
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.2);
}

/* Image Info Overlay */
.image-info-overlay {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  max-width: 90%;
  z-index: 5; /* Ensure it's above the image */
}

.image-counter {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.counter-text {
  color: white;
}

.image-title {
  font-size: 12px;
  font-weight: 500;
  color: white;
  line-height: 1.3;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-link {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #60a5fa;
  font-size: 10px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.source-link:hover {
  color: #93c5fd;
  transform: translateY(-1px);
}

.source-link i {
  font-size: 9px;
}

/* Navigation Buttons */
.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  z-index: 10;
}

.nav-button:hover {
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
}

.nav-button:active {
  transform: translateY(-50%) scale(0.95);
}

.nav-button-prev {
  left: 20px;
}

.nav-button-next {
  right: 20px;
}

.nav-button i {
  font-size: 18px;
}

/* Thumbnail Carousel */
.thumbnail-carousel {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-top: 1px solid #e2e8f0;
  padding: 20px 24px;
}

.carousel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.carousel-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.carousel-title i {
  color: #667eea;
  font-size: 14px;
}

.image-count {
  font-size: 14px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 500;
}

.thumbnail-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 8px 0;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.thumbnail-container::-webkit-scrollbar {
  height: 6px;
}

.thumbnail-container::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.thumbnail-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.thumbnail-container::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.thumbnail-item {
  position: relative;
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  cursor: pointer;
  border-radius: 12px;
  overflow: hidden;
  border: 3px solid transparent;
  transition: all 0.3s ease;
  background: white;
}

.thumbnail-item:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.thumbnail-active {
  border-color: #667eea !important;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  transform: translateY(-2px);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
}

.thumbnail-item:hover .thumbnail-image {
  transform: scale(1.05);
}

.thumbnail-overlay {
  position: absolute;
  top: 4px;
  right: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
}

.thumbnail-item:hover .thumbnail-overlay,
.thumbnail-active .thumbnail-overlay {
  opacity: 1;
}

.thumbnail-number {
  font-size: 10px;
  font-weight: 600;
  color: white;
}

/* iPad Landscape specific adjustments */
@media (min-width: 1024px) and (max-width: 1366px) and (orientation: landscape) {
  .modal-container {
    max-height: 80vh; /* Reduce height on iPad landscape to fit thumbnails */
  }
  
  .image-container {
    min-height: 50vh; /* Reduce image container height */
    max-height: 50vh;
  }
  
  .main-image {
    max-height: 45vh; /* Ensure image fits within container */
  }
  
  .thumbnail-carousel {
    padding: 12px 20px; /* Reduce padding to save space */
  }
  
  .thumbnail-item {
    width: 70px; /* Slightly smaller thumbnails */
    height: 70px;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .modal-container {
    max-width: 95vw;
    max-height: calc(95vh - 64px); /* Reduce height to account for top margin */
    margin: 80px 0 0 0; /* Add top margin to avoid search bar overlap */
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 16px 20px;
  }
  
  .header-icon {
    width: 40px;
    height: 40px;
  }
  
  .header-icon i {
    font-size: 16px;
  }
  
  .modal-title {
    font-size: 20px;
  }
  
  .modal-subtitle {
    font-size: 12px;
  }
  
  .close-button {
    width: 40px;
    height: 40px;
  }
  
  .image-container {
    min-height: 50vh;
  }
  
  .main-image {
    max-height: 50vh;
  }
  
  .nav-button {
    width: 48px;
    height: 48px;
  }
  
  .nav-button-prev {
    left: 12px;
  }
  
  .nav-button-next {
    right: 12px;
  }
  
  .nav-button i {
    font-size: 16px;
  }
  
  .image-info-overlay {
    bottom: -30px; /* Match desktop positioning */
    padding: 6px 12px;
    gap: 8px;
    min-width: 150px;
    width: calc(100% - 24px);
    max-width: 280px;
  }

  .image-counter {
    padding: 3px 8px;
    font-size: 9px;
  }

  .image-title {
    font-size: 11px;
  }

  .source-link {
    font-size: 9px;
  }
  
  .thumbnail-carousel {
    padding: 16px 20px;
  }
  
  .thumbnail-item {
    width: 64px;
    height: 64px;
  }
  
  .thumbnail-overlay {
    width: 18px;
    height: 18px;
  }
  
  .thumbnail-number {
    font-size: 9px;
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .header-content {
    gap: 12px;
  }
  
  .modal-title {
    font-size: 18px;
  }
  
  .image-info-overlay {
    position: absolute;
    bottom: -30px; /* Match desktop and tablet positioning */
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
    padding: 6px 12px;
    gap: 8px;
    background: rgba(0, 0, 0, 0.9);
    width: calc(100% - 24px);
    max-width: 300px;
  }

  .image-counter {
    padding: 3px 8px;
    font-size: 9px;
  }

  .image-title {
    font-size: 10px;
  }

  .source-link {
    font-size: 8px;
  }
  
  .thumbnail-container {
    gap: 8px;
  }
  
  .thumbnail-item {
    width: 56px;
    height: 56px;
  }
}
</style>